
import React, { useState, useEffect, useRef } from 'react';
import { 
  Map, 
  User, 
  Target, 
  Users, 
  Calculator, 
  Zap, 
  RefreshCcw,
  Activity,
  Network,
  Cpu
} from 'lucide-react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, Cell } from 'recharts';

const modules = [
  {
    icon: <Map className="text-[#c17c0c]" size={32} />,
    title: "Módulo 1: Radar Territorial",
    tech: "H3 + PyDeck",
    description: "Visualização geoespacial de alta performance em hexágonos. Diagnóstico cirúrgico de força territorial.",
    details: ["Renderização em tempo real", "Análise de dominância de bairros", "Cruzamento histórico de votação"]
  },
  {
    icon: <User className="text-[#c17c0c]" size={32} />,
    title: "Módulo 2: Perfil de Liderança",
    tech: "Plotly Radar Chart",
    description: "Inteligência competitiva profunda. Entenda suas forças e mapeie o perfil demográfico do eleitor.",
    details: ["Radar de Competências", "Análise Renda/Idade", "Espionagem competitiva baseada em dados"]
  },
  {
    icon: <Target className="text-[#c17c0c]" size={32} />,
    title: "Módulo 3: Finder de Oportunidades",
    tech: "Filtros Dinâmicos",
    description: "Algoritmo 'Oceano Azul'. Identifica zonas de baixa fidelidade e alto potencial de conversão.",
    details: ["Índice de Volatilidade", "HHI (Concentração de Poder)", "Cálculo de Custo por Voto Estimado"]
  },
  {
    icon: <Users className="text-[#c17c0c]" size={32} />,
    title: "Módulo 4: Matriz de Alianças",
    tech: "NetworkX + Plotly",
    description: "Ferramenta de diplomacia tática. IA Advisor sugere aliados com complementaridade territorial.",
    details: ["Grafos de conexões políticas", "Prevenção de sobreposição", "Sugestão automática de alianças"]
  },
  {
    icon: <Calculator className="text-[#c17c0c]" size={32} />,
    title: "Módulo 5: Simulador de Chapa",
    tech: "Pandas + Altair",
    description: "Calculadora eleitoral oficial. Simule distribuição de cadeiras com as regras exatas do TSE.",
    details: ["Cláusula de Barreira 80/20", "Cálculo de Quociente e Sobras", "Compliance Total Res. 23.677"]
  },
  {
    icon: <Zap className="text-[#c17c0c]" size={32} />,
    title: "Módulo 6: Oráculo IA",
    tech: "Multi-LLM Core",
    description: "Consultor sênior 24/7. Chatbot especializado em estratégia política com memória de contexto.",
    details: ["Exportação de relatórios PDF", "Persona 'Cientista Político'", "Insights prescritivos em segundos"]
  }
];

const mockRadarData = [
  { subject: 'Digital', A: 120, B: 110, fullMark: 150 },
  { subject: 'Território', A: 98, B: 130, fullMark: 150 },
  { subject: 'Carisma', A: 86, B: 130, fullMark: 150 },
  { subject: 'Fidelidade', A: 99, B: 100, fullMark: 150 },
  { subject: 'Recursos', A: 85, B: 90, fullMark: 150 },
  { subject: 'Histórico', A: 65, B: 85, fullMark: 150 },
];

const mockBarData = [
  { name: 'P1', value: 45 },
  { name: 'P2', value: 32 },
  { name: 'P3', value: 21 },
  { name: 'P4', value: 15 },
  { name: 'P5', value: 8 },
];

const ModuleSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -80px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setVisibleIndices((prev) => {
            if (prev.has(index)) return prev;
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
          });
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll('.module-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <section id="modules" ref={sectionRef} className="py-24 bg-zinc-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
          <div className="text-left">
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4 italic">
              Arsenal <span className="text-[#c17c0c]">Tecnológico</span>
            </h2>
            <p className="text-zinc-500 max-w-2xl font-light">
              Módulos integrados para uma cobertura 360º do cenário eleitoral. Da análise de hexágonos à simulação matemática de cadeiras.
            </p>
          </div>
          <button 
            onClick={handleRefresh}
            className="flex items-center space-x-2 text-xs font-mono uppercase tracking-widest text-[#c17c0c] border border-[#c17c0c]/20 px-4 py-2 rounded-full bg-[#c17c0c]/5 hover:bg-[#c17c0c]/10 transition-all group"
          >
            <RefreshCcw size={14} className={`group-hover:rotate-180 transition-transform duration-700 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'Sincronizando...' : 'Recarregar Dados'}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {modules.map((module, index) => {
            const isVisible = visibleIndices.has(index);
            return (
              <div 
                key={index}
                data-index={index}
                className={`module-card group p-8 border border-zinc-900 bg-black rounded-2xl relative flex flex-col min-h-[550px] transition-all
                  ${isVisible 
                    ? 'opacity-100 translate-y-0 scale-100 hover:-translate-y-3 hover:border-[#c17c0c]/60 hover:shadow-[0_20px_40px_-15px_rgba(193,124,12,0.25)]' 
                    : 'opacity-0 translate-y-16 scale-[0.96] pointer-events-none'
                  }
                `}
                style={{ 
                  transitionDuration: '800ms',
                  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                  transitionDelay: isVisible ? `${(index % 3) * 120}ms` : '0ms' 
                }}
              >
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  {module.icon}
                </div>

                {/* Chart Scanning Overlay */}
                {isLoading && (
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center p-6 text-center rounded-2xl">
                    <div className="scanline"></div>
                    <Activity className="text-[#c17c0c] animate-pulse mb-4" size={32} />
                    <p className="text-[10px] font-mono text-[#c17c0c] uppercase tracking-widest chart-loading-pulse">
                      Acessando Cloud Core... <br/>
                      {index % 2 === 0 ? 'Mapeando Território' : 'Processando IA Híbrida'}
                    </p>
                  </div>
                )}
                
                <div className="mb-6 bg-zinc-900/50 w-16 h-16 flex items-center justify-center rounded-xl border border-zinc-800 group-hover:bg-[#c17c0c]/10 group-hover:border-[#c17c0c]/30 transition-all duration-500">
                  {module.icon}
                </div>
                
                <span className="text-[10px] font-mono text-[#c17c0c] uppercase tracking-widest block mb-2 font-bold">{module.tech}</span>
                <h3 className="text-xl font-bold mb-4 uppercase italic tracking-tighter">{module.title}</h3>
                <p className="text-zinc-500 text-sm mb-6 leading-relaxed font-light">
                  {module.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {module.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-xs text-zinc-400 font-mono">
                      <div className="w-1 h-1 bg-[#c17c0c] rounded-full mr-2"></div>
                      {detail}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-4 border-t border-zinc-900/50">
                  {/* Module 1 Illustration: Hexagonal Map Grid */}
                  {index === 0 && (
                    <div className="h-32 w-full flex items-center justify-center overflow-hidden">
                      <div className="grid grid-cols-6 gap-1 opacity-20 transform -skew-x-12">
                        {Array.from({length: 18}).map((_, i) => (
                          <div key={i} className={`w-8 h-8 hexagon-mask ${i % 3 === 0 ? 'bg-[#c17c0c]' : 'bg-zinc-800'} animate-pulse`} style={{ animationDelay: `${i * 100}ms` }}></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Module 2 Illustration: Radar Chart */}
                  {index === 1 && (
                    <div className="h-32 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="75%" data={mockRadarData}>
                          <PolarGrid stroke="#27272a" />
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#52525b', fontSize: 7, fontWeight: 'bold' }} />
                          <Radar 
                            name="Candidato" 
                            dataKey="A" 
                            stroke="#c17c0c" 
                            fill="#c17c0c" 
                            fillOpacity={isLoading ? 0.05 : 0.4} 
                            animationDuration={1500}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Module 3 Illustration: Opportunity Heatmap */}
                  {index === 2 && (
                    <div className="h-32 w-full flex items-center justify-center p-4">
                      <div className="w-full h-full border border-zinc-900 rounded bg-zinc-950/50 grid grid-cols-5 grid-rows-4 gap-1 p-1">
                        {Array.from({length: 20}).map((_, i) => (
                          <div key={i} className={`rounded-sm transition-colors duration-1000 ${Math.random() > 0.7 ? 'bg-[#c17c0c]' : Math.random() > 0.4 ? 'bg-[#c17c0c]/30' : 'bg-zinc-900'}`} style={{ opacity: Math.random() * 0.4 + 0.2 }}></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Module 4 Illustration: Matriz de Alianças (Network) */}
                  {index === 3 && (
                    <div className="h-32 w-full flex items-center justify-center">
                      <div className="relative w-full h-full border border-zinc-900 rounded-lg bg-zinc-950 overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center opacity-10">
                          <div className="w-20 h-20 border border-[#c17c0c]/40 rounded-full animate-ping"></div>
                        </div>
                        <div className="relative z-10 w-full h-full flex items-center justify-center">
                           <Network size={36} className="text-[#c17c0c]/20 animate-pulse" />
                        </div>
                        <div className="absolute bottom-2 right-2 text-[7px] font-mono text-[#c17c0c] opacity-30 uppercase">graph_ready: true</div>
                      </div>
                    </div>
                  )}

                  {/* Module 5 Illustration: Simulador de Chapa (Bar Chart) */}
                  {index === 4 && (
                    <div className="h-32 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={mockBarData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                            {mockBarData.map((entry, idx) => (
                              <Cell key={`cell-${idx}`} fill={idx === 0 ? '#c17c0c' : '#18181b'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Module 6 Illustration: Oráculo IA (Waveform/Processing) */}
                  {index === 5 && (
                    <div className="h-32 w-full flex flex-col items-center justify-center space-y-2">
                      <div className="flex items-end space-x-1.5 h-10">
                        {Array.from({length: 10}).map((_, i) => (
                          <div key={i} className="w-1 bg-[#c17c0c]/60 rounded-full animate-bounce" style={{ height: `${20 + Math.random() * 80}%`, animationDuration: `${0.6 + Math.random()}s` }}></div>
                        ))}
                      </div>
                      <div className="flex items-center space-x-2 text-[#c17c0c]/40">
                        <Cpu size={12} className="animate-spin duration-[4000ms]" />
                        <span className="text-[9px] font-mono uppercase tracking-[0.1em]">Protocol Advisor Online</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ModuleSection;
